"use client"

import React, { useState, useContext, useEffect, useMemo } from "react"
import { Link, useLocation } from "react-router-dom"
import { ErpContext } from "../../context/ErpContext"
import { useTenantPath } from "../../hooks/useTenantNavigation"
import Cookies from "js-cookie"
import { ordenarEstructuraMenu } from "../../utils/menuAccesoOrden"

// Material UI
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
  Badge,
  useTheme,
  alpha,
  Button,
  useMediaQuery,
} from "@mui/material"

// Icons
import HomeIcon from "@mui/icons-material/Home"
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn"
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits"
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted"
import AddCircleIcon from "@mui/icons-material/AddCircle"
import ViewListIcon from "@mui/icons-material/ViewList"
import MenuBookIcon from "@mui/icons-material/MenuBook"
import SettingsIcon from "@mui/icons-material/Settings"
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt"
import PeopleAltIcon from "@mui/icons-material/PeopleAlt"
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange"
import LocalAtmIcon from "@mui/icons-material/LocalAtm"
import RequestQuoteIcon from "@mui/icons-material/RequestQuote"
import CardTravelIcon from "@mui/icons-material/CardTravel"
import PaymentsIcon from "@mui/icons-material/Payments"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import ExpandLess from "@mui/icons-material/ExpandLess"
import ExpandMore from "@mui/icons-material/ExpandMore"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import LogoutIcon from "@mui/icons-material/Logout"
import InventoryIcon from "@mui/icons-material/Inventory"
import ReceiptIcon from "@mui/icons-material/Receipt"
import PersonIcon from "@mui/icons-material/Person"
import CloseIcon from "@mui/icons-material/Close"
import SupportAgentIcon from "@mui/icons-material/SupportAgent"
import StorefrontIcon from "@mui/icons-material/Storefront"
import BadgeIcon from "@mui/icons-material/Badge"
import FingerprintIcon from "@mui/icons-material/Fingerprint"
import FactCheckIcon from "@mui/icons-material/FactCheck"
import ScheduleIcon from "@mui/icons-material/Schedule"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import ViewTimelineIcon from "@mui/icons-material/ViewTimeline"
import TuneIcon from "@mui/icons-material/Tune"

// Imágenes
import imgLutenteNav from "../../assets/images/LutenteNavTransparent.png"
import lutenteMini from "../../assets/images/lutenteMini.png"

const drawerWidth = 280

const tieneAcceso = (id, accesos) => {
  return accesos?.some((a) => a.idMenuAcceso === id && a.eliminado === false)
}

/** Permisos de pantallas bajo Inventario (incl. VENTAS8/COMPRAS5: IDs históricos en BD, sin renombrar). */
const INVENTARIO_SUBMENU_IDS = [
  "INVENTARIO1",
  "INVENTARIO2",
  "INVENTARIO3",
  "INVENTARIO5",
  "VENTAS8",
  "COMPRAS5",
]

/** Submenús de Configuración (incluye pantallas con id histórico bajo otro módulo). */
const ADMINISTRACION_SUBMENU_IDS = [
  "ADMINISTRACION1",
  "ADMINISTRACION2",
  "ADMINISTRACION3",
  "ADMINISTRACION5",
  "ADMINISTRACION6",
  "ADMINISTRACION7",
  "INVENTARIO4",
  "TRANSACCIONES1",
  "TRANSACCIONES2",
]

const NavBar = () => {
  const theme = useTheme()
  const isTabletDown = useMediaQuery(theme.breakpoints.down("lg"))
  const location = useLocation()
  const { user, logout, setIsNavbarCollapsed, isNavbarCollapsed, getMenuAcceso, getLogoNavbar } = useContext(ErpContext)
  const [logoSrc, setLogoSrc] = useState(imgLutenteNav)
  const [logoMiniSrc, setLogoMiniSrc] = useState(lutenteMini)
  const buildPath = useTenantPath()

  // Estado para Drawer móvil
  const [mobileOpen, setMobileOpen] = useState(false)

  // Estados para gestos de swipe
  const [touchStart, setTouchStart] = useState({ x: null, y: null })
  const [touchEnd, setTouchEnd] = useState({ x: null, y: null })
  const minSwipeDistance = 50 // Distancia mínima para considerar un swipe

  // Estado para los menús expandidos
  const [openMenus, setOpenMenus] = useState({
    VENTAS: false,
    INVENTARIO: false,
    ECOMMERCE: false,
    COMPRAS: false,
    TRANSACCION: false,
    RRHH: false,
    ADMINISTRACION: false,
    SOPORTE: false,
  })

  // Estado para el elemento activo
  const [activeItem, setActiveItem] = useState("")

  // Estado para las descripciones de menuAcceso
  const [menuDescripciones, setMenuDescripciones] = useState({})

  // Logo configurable (fallback Lutente)
  useEffect(() => {
    if (!getLogoNavbar) return

    let cancelled = false
    ;(async () => {
      const logoBD = await getLogoNavbar()
      if (!cancelled && logoBD) {
        setLogoSrc(logoBD)
        setLogoMiniSrc(logoBD)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [getLogoNavbar])

  // Funciones para manejar gestos de swipe
  const onTouchStart = (e) => {
    setTouchEnd({ x: null, y: null })
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    })
  }

  const onTouchMove = (e) => {
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    })
  }

  const onTouchEnd = () => {
    // Si no hay valores válidos de touchEnd, significa que no hubo movimiento (es un click)
    if (!touchStart.x || !touchEnd.x || !touchEnd.y) {
      // Resetear y permitir que el click normal funcione
      setTouchStart({ x: null, y: null })
      setTouchEnd({ x: null, y: null })
      return
    }
    
    const distanceX = touchStart.x - touchEnd.x
    const distanceY = touchStart.y - touchEnd.y
    
    // Calcular la distancia total del movimiento
    const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY)
    
    // Si el movimiento es muy pequeño (menos de 10px), es un tap/click, no un swipe
    if (totalDistance < 10) {
      setTouchStart({ x: null, y: null })
      setTouchEnd({ x: null, y: null })
      return
    }
    
    // Calcular si el movimiento es más horizontal que vertical
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY)
    
    // Solo procesar si es un swipe horizontal predominante
    if (!isHorizontalSwipe) {
      setTouchStart({ x: null, y: null })
      setTouchEnd({ x: null, y: null })
      return
    }
    
    const isLeftSwipe = distanceX > minSwipeDistance
    const isRightSwipe = distanceX < -minSwipeDistance
    
    // Swipe desde el borde izquierdo hacia la derecha abre el navbar
    if (isRightSwipe && touchStart.x < 50) {
      setMobileOpen(true)
    }
    
    // Swipe hacia la izquierda cierra el navbar
    if (isLeftSwipe && mobileOpen) {
      setMobileOpen(false)
    }
    
    // Resetear estados
    setTouchStart({ x: null, y: null })
    setTouchEnd({ x: null, y: null })
  }

  // Efecto para cargar las descripciones de menuAcceso
  useEffect(() => {
    const cargarMenuDescripciones = async () => {
      try {
        const menusData = await getMenuAcceso()
        console.log(menusData)
        if (menusData) {
          // Crear un objeto con id como clave y descripcion como valor
          const descripciones = {}
          menusData.forEach(menu => {
            descripciones[menu.id] = menu.descripcion
          })
          setMenuDescripciones(descripciones)
        }
      } catch (error) {
        console.error("Error al cargar descripciones de menú:", error)
      }
    }

    cargarMenuDescripciones()
  }, [])

  // Efecto para cerrar menús cuando se hace clic fuera del navbar colapsado
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Solo aplicar en modo colapsado (desktop) y cuando hay menús abiertos
      if (isNavbarCollapsed && !isTabletDown && Object.values(openMenus).some(open => open)) {
        // Verificar si el clic fue dentro del navbar o dropdown
        const navbarElement = document.querySelector('[role="navigation"]')
        const dropdownElements = document.querySelectorAll('[data-dropdown="true"]')
        
        let clickedInsideNavbarOrDropdown = false
        
        // Verificar si el clic fue dentro del navbar
        if (navbarElement && navbarElement.contains(event.target)) {
          clickedInsideNavbarOrDropdown = true
        }
        
        // Verificar si el clic fue dentro de algún dropdown flotante
        dropdownElements.forEach(dropdown => {
          if (dropdown.contains(event.target)) {
            clickedInsideNavbarOrDropdown = true
          }
        })
        
        // Si el clic fue fuera del navbar y fuera de los dropdowns, cerrar todos los menús
        if (!clickedInsideNavbarOrDropdown) {
          setOpenMenus({
            VENTAS: false,
            INVENTARIO: false,
            COMPRAS: false,
            TRANSACCION: false,
            RRHH: false,
            ADMINISTRACION: false,
            SOPORTE: false,
          })
        }
      }
    }

    // Agregar event listener
    document.addEventListener('mousedown', handleClickOutside)
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isNavbarCollapsed, isTabletDown, openMenus])

  // Efecto para determinar el elemento activo basado en la ruta actual. esto es solo visual. *****
  useEffect(() => {
    const raw = location.pathname.replace(/\/$/, "") || "/"
    const segments = raw.split("/").filter(Boolean)
    const path =
      segments.length >= 2 ? `/${segments.slice(1).join("/")}` : raw.startsWith("/") ? raw : `/${raw}`

    // Mapeo de rutas a elementos del menú
    const routeToMenuItem = {
      "/": "DASHBOARD",
      "/manual": "MANUAL",
      "/gestionGenerarVenta": "VENTAS1",
      "/tablaVentas": "VENTAS2",
      "/gestionGenerarPresupuesto": "VENTAS3",
      "/tablaPresupuesto": "VENTAS4",
      "/gestionCliente": "VENTAS7",
      "/ventaCrear": "VENTAS10",
      "/preventaCrear": "VENTAS11",
      "/tablaPreventas": "VENTAS12",
      "/INVENTARIO3": "INVENTARIO3",
      "/ecommerce": "ECOMMERCE2",
      "/ecommerceParametros": "ECOMMERCE3",
      "/INVENTARIO4": "INVENTARIO4",
      "/Atributos": "INVENTARIO4",
      "/itemProveedor": "itemProveedor",
      "/COMPRAS5": "COMPRAS5",
      "/tablaListaCosto": "COMPRAS5",
      "/tablaListaPrecio": "VENTAS8",
      "/INVENTARIO1": "INVENTARIO1",
      "/crearCompra": "COMPRAS_GRUPO_COMPRAS",
      "/tablaCompra": "COMPRAS_GRUPO_COMPRAS",
      "/verCompraDetalle": "COMPRAS_GRUPO_COMPRAS",
      "/gestionMoneda": "ADMINISTRACION1",
      "/gestionMedioPago": "TRANSACCIONES1",
      "/gestionTipoTransaccion": "TRANSACCIONES2",
      "/Impuestos": "ADMINISTRACION2",
      "/gestionCategoria": "ADMINISTRACION3",
      "/tablaTransaccionComponent": "TRANSACCIONES5",
      "/tablaCuentaCorriente": "TRANSACCIONES6",
      "/gestionUbicaciones": "ADMINISTRACION5",
      "/gestionEntidad": "ADMINISTRACION6",
      "/gestionUsuarios": "ADMINISTRACION7",
      "/TablaEntidadProveedor": "COMPRAS4",
      "/tablaVentasAfip": "TRANSACCIONES7",
      "/cajaComponentAnaliticos": "TRANSACCIONES8",
      "/cajaComponent": "TRANSACCIONES10",
      "/recetaComponent": "INVENTARIO2",
      "/tablaReceta": "INVENTARIO5",
      "/gestionGastosComponent": "TRANSACCIONES9",
      "/tablaGastosComponent": "TRANSACCIONES9",
      "/tablaVentasEstados": "VENTAS9",
      "/rrhh/empleados": "RRHH1",
      "/rrhh/turnos": "RRHH2",
      "/rrhh/fichajes": "RRHH3",
      "/rrhh/asistencia": "RRHH4",
      "/rrhh/liquidaciones": "RRHH5",
      "/rrhh/reportes": "RRHH6",
      "/rrhh/plantillas": "RRHH7",
      "/rrhh/calendario": "RRHH8",
      "/rrhh/timeline": "RRHH9",
      "/rrhh/configuracion": "RRHH10",
      "/soporte/crear": "SOPORTE1",
      "/soporte/tickets": "SOPORTE2",
      "/soporte/ticket": "SOPORTE2",
      "/soporte/bandejas": "SOPORTE3",
      "/soporte/metricas": "SOPORTE4",
      "/soporte/configuracion": "SOPORTE5",
    }

    // Mapeo de elementos del menú a sus secciones padre
    const menuItemToSection = {
      VENTAS1: "VENTAS",
      VENTAS2: "VENTAS",
      VENTAS3: "VENTAS",
      VENTAS4: "VENTAS",
      VENTAS5: "VENTAS",
      VENTAS6: "VENTAS",
      VENTAS9: "VENTAS",
      VENTAS7: "VENTAS",
      VENTAS10: "VENTAS",
      VENTAS11: "VENTAS",
      VENTAS12: "VENTAS",
      INVENTARIO3: "INVENTARIO",
      ECOMMERCE2: "ECOMMERCE",
      ECOMMERCE3: "ECOMMERCE",
      INVENTARIO4: "ADMINISTRACION",
      INVENTARIO1: "INVENTARIO",
      itemProveedor: "INVENTARIO",
      INVENTARIO2: "INVENTARIO",
      INVENTARIO5: "INVENTARIO",
      VENTAS8: "INVENTARIO",
      COMPRAS5: "INVENTARIO",
      COMPRAS_GRUPO_COMPRAS: "COMPRAS",
      COMPRAS1: "COMPRAS",
      COMPRAS2: "COMPRAS",
      COMPRAS3: "COMPRAS",
      COMPRAS4: "COMPRAS",
      TRANSACCIONES1: "ADMINISTRACION",
      TRANSACCIONES2: "ADMINISTRACION",
      TRANSACCIONES5: "TRANSACCION",
      TRANSACCIONES6: "TRANSACCION",
      TRANSACCIONES7: "TRANSACCION",
      TRANSACCIONES8: "TRANSACCION",
      TRANSACCIONES9: "TRANSACCION",
      TRANSACCIONES10: "TRANSACCION",
      RRHH1: "RRHH",
      RRHH2: "RRHH",
      RRHH3: "RRHH",
      RRHH4: "RRHH",
      RRHH5: "RRHH",
      RRHH6: "RRHH",
      RRHH7: "RRHH",
      RRHH8: "RRHH",
      RRHH9: "RRHH",
      RRHH10: "RRHH",
      ADMINISTRACION1: "ADMINISTRACION",
      ADMINISTRACION2: "ADMINISTRACION",
      ADMINISTRACION3: "ADMINISTRACION",
      ADMINISTRACION5: "ADMINISTRACION",
      ADMINISTRACION6: "ADMINISTRACION",
      ADMINISTRACION7: "ADMINISTRACION",
      SOPORTE1: "SOPORTE",
      SOPORTE2: "SOPORTE",
      SOPORTE3: "SOPORTE",
      SOPORTE4: "SOPORTE",
      SOPORTE5: "SOPORTE",
    }

    const menuItem = routeToMenuItem[path]
    if (menuItem) {
      setActiveItem(menuItem)
      const parentSection = menuItemToSection[menuItem]
      if (parentSection) {
        setOpenMenus((prev) => ({
          ...prev,
          [parentSection]: true,
        }))
      }
    }
  }, [location.pathname])

  // Función para alternar colapso en desktop
  const toggleNavbar = () => {
    // Si estamos colapsando la barra, cerramos todos los menús
    if (!isNavbarCollapsed) {
      setOpenMenus({
        VENTAS: false,
        INVENTARIO: false,
        COMPRAS: false,
        TRANSACCION: false,
        RRHH: false,
        ADMINISTRACION: false,
        SOPORTE: false,
      })
    }
    setIsNavbarCollapsed(!isNavbarCollapsed)
  }

  // Toggle de submenús
  const handleToggleMenu = (menu) => {
    if (isNavbarCollapsed) {
      const newOpen = {
        VENTAS: false,
        INVENTARIO: false,
        COMPRAS: false,
        TRANSACCION: false,
        RRHH: false,
        ADMINISTRACION: false,
        SOPORTE: false,
      }
      newOpen[menu] = !openMenus[menu]
      setOpenMenus(newOpen)
    } else {
      setOpenMenus((prev) => ({ ...prev, [menu]: !prev[menu] }))
    }
  }

  // Clic en ítem
  const handleMenuItemClick = (item) => {
    setActiveItem(item)
    // En móvil, cerrar el Drawer tras navegar
    if (isTabletDown) {
      setMobileOpen(false)
    }
  }

  // Función helper para obtener la descripción del menú desde la BD o usar el label por defecto
  const getMenuLabel = (id, defaultLabel) => {
    // Nombre oficial del menú (reemplaza textos antiguos en BD, p. ej. "Ventas AFIP")
    if (id === "TRANSACCIONES7") {
      return "Ventas ARCA";
    }
    // Forzar nombre del módulo principal de soporte
    if (id === 'SOPORTE') {
      return 'Soporte';
    }
    // Forzar nombre del submenú de bandejas a TICKETS
    if (id === 'SOPORTE3') {
      return 'TICKETS';
    }
    // Para SOPORTE2, cambiar el nombre según el rol del usuario
    if (id === 'SOPORTE2') {
      const userRole = Cookies.get('rolUsuario') || user?.rolUsuario || '';
      const esAgente = userRole === 'Agente Soporte' || userRole === 'Soporte';
      const esAdmin = userRole === 'Administrador';
      if (esAgente || esAdmin) {
        return menuDescripciones[id] || 'Todos los Tickets';
      }
    }
    return menuDescripciones[id] || defaultLabel
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Estructura del menú generada dinámicamente usando las descripciones de la BD
  // ───────────────────────────────────────────────────────────────────────────
  // ⚠️ CONVENCIÓN DE `groupPaths` EN NODOS GRUPO ⚠️
  //
  // Los nodos grupo (id MODULO_GRUPO_NOMBRE, con `groupAccessIds`) resuelven la
  // ruta destino vía `getGroupPath()`:
  //   1) Lee `localStorage[localStorageKey]` (última ruta visitada del grupo).
  //   2) Si no hay memoria válida, itera `groupPaths` en orden y toma el
  //      PRIMER segmento cuya pantalla sea accesible (`pathToAccessId` + `tieneAcceso`).
  //   3) Último recurso: `defaultPath` (no se valida contra accesos).
  //
  // Consecuencia: **el orden de `groupPaths` importa** — el primer segmento
  // accesible es el "landing" efectivo. Reglas al definir uno nuevo:
  //
  //   • Si el grupo tiene un componente `*NavTabs` que escribe `localStorageKey`
  //     (Ventas, Presupuesto, Compras → ver `VentasNavTabs.js`, `PresupuestoNavTabs.js`,
  //     `ComprasNavTabs.js`), el primer `groupPaths[0]` es solo el landing inicial;
  //     a partir de la primera navegación el localStorage recuerda y pisa el orden.
  //     Convención actual: "crear" primero para esos módulos transaccionales.
  //
  //   • Si el grupo NO tiene NavTabs (Inventario, Receta), nadie escribe nunca
  //     el localStorage → el paso 1 devuelve siempre null → el orden de
  //     `groupPaths` es la única fuente de verdad del landing. En esos grupos
  //     poné PRIMERO la pantalla de listado (que es lo que el usuario espera
  //     al hacer clic en el sidebar), y el "crear" como fallback.
  //
  // Ver `SISTEMA-SEGURIDAD.md` → sección "NavBar.js — Comportamiento completo"
  // (subsecciones 4. getGroupPath y 7. fuentes de verdad).
  // ═══════════════════════════════════════════════════════════════════════════
  const menuStructure = useMemo(() => ordenarEstructuraMenu([
    {
      id: "DASHBOARD",
      label: getMenuLabel("DASHBOARD", "Inicio"),
      icon: <HomeIcon />,
      path: buildPath(""),
      allowedRoles: ["Administrador", "Vendedor"],
      children: [],
    },
    {
      id: "VENTAS",
      label: getMenuLabel("VENTAS", "Ventas"),
      icon: <MonetizationOnIcon />,
      children: [
        {
          id: "VENTAS_GRUPO_VENTAS",
          label: "Ventas",
          icon: <ShoppingCartIcon />,
          groupAccessIds: ["VENTAS1", "VENTAS2", "VENTAS5"],
          // Landing inicial: "crear venta" (grupo transaccional con VentasNavTabs.js
          // que persiste la última pestaña visitada en `lutenteLastVentasPath`).
          groupPaths: ["gestionGenerarVenta", "tablaVentas", "verVentasDetalle"],
          localStorageKey: "lutenteLastVentasPath",
          defaultPath: buildPath("gestionGenerarVenta"),
        },
        {
          id: "VENTAS_GRUPO_PRESUPUESTO",
          label: "Presupuesto",
          icon: <ReceiptIcon />,
          groupAccessIds: ["VENTAS3", "VENTAS4", "VENTAS6"],
          // Landing inicial: "crear presupuesto" (grupo transaccional con
          // PresupuestoNavTabs.js que persiste la última pestaña).
          groupPaths: ["gestionGenerarPresupuesto", "tablaPresupuesto", "verPresupuestoDetalle"],
          localStorageKey: "lutenteLastPresupuestoPath",
          defaultPath: buildPath("gestionGenerarPresupuesto"),
        },
        {
          id: "VENTAS7",
          label: getMenuLabel("VENTAS7", "Clientes"),
          icon: <ViewListIcon />,
          path: buildPath("gestionCliente"),
          allowedRoles: ["Administrador", "Vendedor"],
        },
        {
          id: "VENTAS9",
          label: getMenuLabel("VENTAS9", "Pedidos"),
          icon: <ViewListIcon />,
          path: buildPath("tablaVentasEstados"),
          allowedRoles: ["Administrador"],
        },
        // VENTAS10 (Crear Venta Nuevo), VENTAS11 (Crear Preventa), VENTAS12 (Ver Preventas) — ocultos (eliminado: true en validador)
      ],
    },
    {
      id: "INVENTARIO",
      label: getMenuLabel("INVENTARIO", "Inventario"),
      icon: <FormatListBulletedIcon />,
      children: [
        {
          id: "INVENTARIO_GRUPO_ITEMS",
          label: "Stock",
          icon: <InventoryIcon />,
          groupAccessIds: ["INVENTARIO1", "INVENTARIO3"],
          // ⚠️ Orden intencional: tabla de stock (INVENTARIO3) PRIMERO, crear ítem
          // (INVENTARIO1) como fallback. No existe InventarioNavTabs, nadie escribe
          // `lutenteLastInventarioPath`, así que este orden ES el landing efectivo:
          // el usuario que clickea "Stock" en el sidebar espera ver el listado, no
          // el formulario de alta. No invertir sin reemplazar también `defaultPath`
          // y/o crear un NavTabs que persista la última ruta.
          groupPaths: ["inventario", "crearItem"],
          pathToAccessId: { inventario: "INVENTARIO3", crearItem: "INVENTARIO1" },
          localStorageKey: "lutenteLastInventarioPath",
          defaultPath: buildPath("inventario"),
        },
        {
          id: "INVENTARIO_GRUPO_RECETA",
          label: "Receta",
          icon: <ReceiptIcon />,
          groupAccessIds: ["INVENTARIO2", "INVENTARIO5"],
          // ⚠️ Orden intencional (mismo criterio que INVENTARIO_GRUPO_ITEMS): tabla
          // de recetas (INVENTARIO5) primero, crear receta (INVENTARIO2) como
          // fallback. No hay RecetaNavTabs que escriba `lutenteLastRecetaPath`.
          groupPaths: ["tablaReceta", "recetaComponent"],
          pathToAccessId: { tablaReceta: "INVENTARIO5", recetaComponent: "INVENTARIO2" },
          localStorageKey: "lutenteLastRecetaPath",
          defaultPath: buildPath("tablaReceta"),
        },
        {
          id: "VENTAS8",
          label: getMenuLabel("VENTAS8", "Lista de Precios"),
          icon: <ViewListIcon />,
          path: buildPath("tablaListaPrecio"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "COMPRAS5",
          label: getMenuLabel("COMPRAS5", "Lista Costo"),
          icon: <ViewListIcon />,
          path: buildPath("tablaListaCosto"),
          allowedRoles: ["Administrador"],
        },
      ],
    },
    {
      id: "COMPRAS",
      label: getMenuLabel("COMPRAS", "Compras"),
      icon: <ShoppingCartIcon />,
      children: [
        {
          id: "COMPRAS_GRUPO_COMPRAS",
          label: "Compras",
          icon: <ProductionQuantityLimitsIcon />,
          groupAccessIds: ["COMPRAS1", "COMPRAS2", "COMPRAS3"],
          // Landing inicial: "crear compra" (grupo transaccional con ComprasNavTabs.js
          // que persiste la última pestaña visitada en `lutenteLastComprasPath`).
          groupPaths: ["crearCompra", "tablaCompra", "verCompraDetalle"],
          pathToAccessId: { crearCompra: "COMPRAS1", tablaCompra: "COMPRAS2", verCompraDetalle: "COMPRAS3" },
          localStorageKey: "lutenteLastComprasPath",
          defaultPath: buildPath("crearCompra"),
        },
        {
          id: "COMPRAS4",
          label: getMenuLabel("COMPRAS4", "Proveedores"),
          icon: <ViewListIcon />,
          path: buildPath("TablaEntidadProveedor"),
          allowedRoles: ["Administrador"],
        },
      ],
    },
    {
      id: "TRANSACCION",
      label: getMenuLabel("TRANSACCION", "Administración"),
      icon: <CurrencyExchangeIcon />,
      children: [
        {
          id: "TRANSACCIONES10",
          label: getMenuLabel("TRANSACCIONES10", "Caja"),
          icon: <PaymentsIcon />,
          path: buildPath("cajaComponent"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES9",
          label: getMenuLabel("TRANSACCIONES9", "Gastos"),
          icon: <PaymentsIcon />,
          path: buildPath("tablaGastosComponent"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES5",
          label: getMenuLabel("TRANSACCIONES5", "Transacciones"),
          icon: <PaymentsIcon />,
          path: buildPath("tablaTransaccionComponent"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES6",
          label: getMenuLabel("TRANSACCIONES6", "Cuentas corrientes"),
          icon: <PaymentsIcon />,
          path: buildPath("tablaCuentaCorriente"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES7",
          label: getMenuLabel("TRANSACCIONES7", "Ventas ARCA"),
          icon: <PaymentsIcon />,
          path: buildPath("tablaVentasAfip"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES8",
          label: getMenuLabel("TRANSACCIONES8", "Analíticos"),
          icon: <PaymentsIcon />,
          path: buildPath("cajaComponentAnaliticos"),
          allowedRoles: ["Administrador"],
        },
      ],
    },
    {
      id: "RRHH",
      label: getMenuLabel("RRHH", "Capital Humano"),
      icon: <PersonIcon />,
      children: [
        {
          id: "RRHH1",
          label: getMenuLabel("RRHH1", "Empleados"),
          icon: <BadgeIcon />,
          path: buildPath("rrhh/empleados"),
          allowedRoles: ["Administrador"],
        },
        // {
        //   id: "RRHH2",
        //   label: getMenuLabel("RRHH2", "Turnos"),
        //   icon: <ScheduleIcon />,
        //   path: buildPath("rrhh/turnos"),
        //   allowedRoles: ["Administrador"],
        // },
        {
          id: "RRHH3",
          label: getMenuLabel("RRHH3", "Fichajes"),
          icon: <FingerprintIcon />,
          path: buildPath("rrhh/fichajes"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "RRHH4",
          label: getMenuLabel("RRHH4", "Asistencia"),
          icon: <FactCheckIcon />,
          path: buildPath("rrhh/asistencia"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "RRHH7",
          label: getMenuLabel("RRHH7", "Plantillas"),
          icon: <ScheduleIcon />,
          path: buildPath("rrhh/plantillas"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "RRHH8",
          label: getMenuLabel("RRHH8", "Calendario"),
          icon: <CalendarMonthIcon />,
          path: buildPath("rrhh/calendario"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "RRHH9",
          label: getMenuLabel("RRHH9", "Timeline"),
          icon: <ViewTimelineIcon />,
          path: buildPath("rrhh/timeline"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "RRHH10",
          label: getMenuLabel("RRHH10", "Configuración"),
          icon: <TuneIcon />,
          path: buildPath("rrhh/configuracion"),
          allowedRoles: ["Administrador"],
        },
        // {
        //   id: "RRHH5",
        //   label: getMenuLabel("RRHH5", "Liquidaciones"),
        //   icon: <ReceiptLongIcon />,
        //   path: buildPath("rrhh/liquidaciones"),
        //   allowedRoles: ["Administrador"],
        // },
        // {
        //   id: "RRHH6",
        //   label: getMenuLabel("RRHH6", "Reportes"),
        //   icon: <AssessmentIcon />,
        //   path: buildPath("rrhh/reportes"),
        //   allowedRoles: ["Administrador"],
        // },
      ],
    },
    {
      id: "ADMINISTRACION",
      label: getMenuLabel("ADMINISTRACION", "Configuración"),
      icon: <SettingsIcon />,
      children: [
        {
          id: "ADMINISTRACION5",
          label: getMenuLabel("ADMINISTRACION5", "General"),
          icon: <AddLocationAltIcon />,
          path: buildPath("gestionUbicaciones"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "ADMINISTRACION2",
          label: getMenuLabel("ADMINISTRACION2", "Impuestos"),
          icon: <PaymentsIcon />,
          path: buildPath("Impuestos"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "ADMINISTRACION3",
          label: getMenuLabel("ADMINISTRACION3", "Categoria Gastos"),
          icon: <CardTravelIcon />,
          path: buildPath("gestionCategoria"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "ADMINISTRACION6",
          label: getMenuLabel("ADMINISTRACION6", "Entidad"),
          icon: <PeopleAltIcon />,
          path: buildPath("gestionEntidad"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "ADMINISTRACION7",
          label: getMenuLabel("ADMINISTRACION7", "Usuarios"),
          icon: <PeopleAltIcon />,
          path: buildPath("gestionUsuarios"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "INVENTARIO4",
          label: getMenuLabel("INVENTARIO4", "Atributos de Items"),
          icon: <ViewListIcon />,
          path: buildPath("Atributos"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES1",
          label: getMenuLabel("TRANSACCIONES1", "Medios de pago"),
          icon: <PaymentsIcon />,
          path: buildPath("gestionMedioPago"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES2",
          label: getMenuLabel("TRANSACCIONES2", "Tipos de transacción"),
          icon: <RequestQuoteIcon />,
          path: buildPath("gestionTipoTransaccion"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "ADMINISTRACION1",
          label: getMenuLabel("ADMINISTRACION1", "Monedas"),
          icon: <LocalAtmIcon />,
          path: buildPath("gestionMoneda"),
          allowedRoles: ["Administrador"],
        },
      ],
    },
    {
      id: "SOPORTE",
      label: getMenuLabel("SOPORTE", "Soporte"),
      icon: <SupportAgentIcon />,
      children: [
        {
          id: "SOPORTE1",
          label: getMenuLabel("SOPORTE1", "Crear Ticket"),
          icon: <AddCircleIcon />,
          path: buildPath("soporte/crear"),
          description: "Reportar problema o solicitud",
        },
        {
          id: "SOPORTE2",
          label: getMenuLabel("SOPORTE2", "Mis Tickets"),
          icon: <ViewListIcon />,
          path: buildPath("soporte/tickets"),
          description: "Ver mis tickets",
        },
        {
          id: "SOPORTE3",
          label: getMenuLabel("SOPORTE3", "Bandejas"),
          icon: <FormatListBulletedIcon />,
          path: buildPath("soporte/bandejas"),
          description: "Bandejas de agente",
          allowedRoles: ["Administrador", "Agente Soporte"],
        },
        {
          id: "SOPORTE4",
          label: getMenuLabel("SOPORTE4", "Métricas"),
          icon: <ProductionQuantityLimitsIcon />,
          path: buildPath("soporte/metricas"),
          description: "Estadísticas de soporte",
          allowedRoles: ["Administrador", "Agente Soporte"],
        },
        {
          id: "SOPORTE5",
          label: getMenuLabel("SOPORTE5", "Configuración"),
          icon: <SettingsIcon />,
          path: buildPath("soporte/configuracion"),
          description: "Configurar sistema de soporte",
          allowedRoles: ["Administrador"],
        },
      ],
    },
    {
      id: "ECOMMERCE",
      label: getMenuLabel("ECOMMERCE", "Ecommerce"),
      icon: <StorefrontIcon />,
      children: [
        {
          id: "ECOMMERCE2",
          label: getMenuLabel("ECOMMERCE2", "Publicaciones"),
          icon: <StorefrontIcon />,
          path: buildPath("ecommerce"),
          description: "Items publicados en la tienda online",
        },
        {
          id: "ECOMMERCE3",
          label: getMenuLabel("ECOMMERCE3", "Parámetros"),
          icon: <TuneIcon />,
          path: buildPath("ecommerceParametros"),
          description: "Configuración de la tienda online",
        },
      ],
    },
    {
      // Manual de usuario: ítem top-level, link directo (sin submenú).
      // alwaysVisible ⇒ se salta el filtro por accesos: lo ve cualquier usuario logueado.
      id: "MANUAL",
      label: getMenuLabel("MANUAL", "Manual de usuario"),
      icon: <MenuBookIcon />,
      path: buildPath("manual"),
      alwaysVisible: true,
      children: [],
    },
  ]), [menuDescripciones, buildPath])

  const userAccesses = user?.accesos || []

  const isChildActive = (child) => {
    if (!child.groupPaths) return activeItem === child.id
    const currentSegment = location.pathname.split("/").filter(Boolean).pop()
    return child.groupPaths.includes(currentSegment)
  }

  const getGroupPath = (child) => {
    const saved = localStorage.getItem(child.localStorageKey)
    const pathToAccess = child.pathToAccessId

    const resolveSegment = (segment) => {
      if (!segment || !child.groupPaths.includes(segment)) return null
      const accessId = pathToAccess?.[segment]
      if (accessId && !tieneAcceso(accessId, userAccesses)) return null
      return buildPath(segment)
    }

    const fromSaved = resolveSegment(saved)
    if (fromSaved) return fromSaved

    for (const p of child.groupPaths) {
      const hit = resolveSegment(p)
      if (hit) return hit
    }

    return child.defaultPath
  }

  // Filtrado: mostrar solo menús y submenús con acceso
  const filteredMenu = menuStructure
    .map((menu) => {
      const filteredChildren =
        menu.children && menu.children.length > 0
          ? menu.children.filter((child) => {
              if (child.groupAccessIds) {
                const puedeGrupo = child.id ? tieneAcceso(child.id, userAccesses) : true
                return (
                  puedeGrupo &&
                  child.groupAccessIds.some((id) => tieneAcceso(id, userAccesses))
                )
              }
              return tieneAcceso(child.id, userAccesses)
            })
          : []
      return { ...menu, children: filteredChildren }
    })
    .filter((menu) => {
      // Ítems siempre visibles (ej. Manual de usuario): no dependen de accesos en DB.
      if (menu.alwaysVisible) return true
      let puedeVerPadre = tieneAcceso(menu.id, userAccesses)
      if (menu.id === "INVENTARIO" && !puedeVerPadre) {
        puedeVerPadre = INVENTARIO_SUBMENU_IDS.some((id) => tieneAcceso(id, userAccesses))
      }
      if (menu.id === "ADMINISTRACION" && !puedeVerPadre) {
        puedeVerPadre = ADMINISTRACION_SUBMENU_IDS.some((id) => tieneAcceso(id, userAccesses))
      }
      if (!puedeVerPadre) return false
      if (menu.children.length === 0) return true
      return menu.children.length > 0
    })

  // Contenido común del Drawer (desktop y móvil)
  const DrawerContent = ({ isMobileMode = false }) => (
    <Box
      role="navigation"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Logo y controles */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isNavbarCollapsed && !isMobileMode ? "center" : "space-between",
          padding: 0,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        {isNavbarCollapsed && !isMobileMode ? (
          <img
            src={logoMiniSrc || "/placeholder.svg"}
            alt="Small Logo"
            style={{ height: 35, margin: 16, cursor: "pointer" }}
            onClick={toggleNavbar}
          />
        ) : (
          <img
            src={logoSrc || "/placeholder.svg"}
            alt="Logo"
            style={{ height: 35, margin: 16, cursor: "pointer" }}
            onClick={isMobileMode ? () => setMobileOpen(false) : toggleNavbar}
          />
        )}

        {/* Botón de colapso en desktop y botón de cierre en móvil */}
        {!isMobileMode ? (
          !isNavbarCollapsed && (
            <IconButton onClick={toggleNavbar} size="small" aria-label="Colapsar menú">
              <ChevronLeftIcon />
            </IconButton>
          )
        ) : (
          <IconButton onClick={() => setMobileOpen(false)} size="small" aria-label="Cerrar menú">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Menú de navegación */}
      <List
        sx={{
          padding: 1,
          flex: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.palette.text.primary, 0.2),
            borderRadius: "4px",
          },
        }}
      >
        {filteredMenu.map((menu) => (
          <React.Fragment key={menu.id}>
            {menu.children.length === 0 ? (
              // Elemento sin hijos (ej. Dashboard)
              <ListItem disablePadding sx={{ display: "block", mb: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to={menu.path}
                  onClick={() => handleMenuItemClick(menu.id)}
                  selected={activeItem === menu.id}
                  sx={{
                    minHeight: 48,
                    justifyContent: isNavbarCollapsed && !isMobileMode ? "center" : "initial",
                    px: 2.5,
                    borderRadius: "8px",
                    backgroundColor: activeItem === menu.id ? "rgb(244, 177, 14)" : "transparent",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                    "&.Mui-selected": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.15),
                      },
                    },
                  }}
                >
                  <Tooltip title={isNavbarCollapsed && !isMobileMode ? menu.label : ""} placement="right">
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: isNavbarCollapsed && !isMobileMode ? 0 : 2,
                        justifyContent: "center",
                        color: activeItem === menu.id ? "rgb(244, 177, 14)" : "inherit",
                      }}
                    >
                      {menu.icon}
                    </ListItemIcon>
                  </Tooltip>
                  {(!isNavbarCollapsed || isMobileMode) && (
                    <ListItemText
                      primary={menu.label}
                      primaryTypographyProps={{
                        fontWeight: activeItem === menu.id ? 600 : 400,
                        color: activeItem === menu.id ? "rgb(244, 177, 14)" : "inherit",
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            ) : (
              // Elemento con submenú
              <>
                <ListItem disablePadding sx={{ display: "block", mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleToggleMenu(menu.id)}
                    sx={{
                      minHeight: 48,
                      justifyContent: isNavbarCollapsed && !isMobileMode ? "center" : "initial",
                      px: 2.5,
                      borderRadius: "8px",
                      backgroundColor: openMenus[menu.id] ? alpha(theme.palette.primary.main, 0.05) : "transparent",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <Tooltip title={isNavbarCollapsed && !isMobileMode ? menu.label : ""} placement="right">
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: isNavbarCollapsed && !isMobileMode ? 0 : 2,
                          justifyContent: "center",
                          color: openMenus[menu.id] ? "rgb(244, 177, 14)" : "inherit",
                        }}
                      >
                        <Badge
                          color="default"
                          variant="dot"
                          invisible={!menu.children.some((child) => isChildActive(child))}
                        >
                          {menu.icon}
                        </Badge>
                      </ListItemIcon>
                    </Tooltip>
                    {(!isNavbarCollapsed || isMobileMode) && (
                      <>
                        <ListItemText
                          primary={menu.label}
                          primaryTypographyProps={{
                            fontWeight: openMenus[menu.id] ? 600 : 400,
                            color: openMenus[menu.id] ? "rgb(244, 177, 14)" : "inherit",
                          }}
                        />
                        {openMenus[menu.id] ? <ExpandLess /> : <ExpandMore />}
                      </>
                    )}
                  </ListItemButton>
                </ListItem>

                {/* Submenú en desktop expandido */}
                {!isNavbarCollapsed && !isMobileMode && (
                  <Collapse in={openMenus[menu.id]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {menu.children.map((child) => (
                        <ListItemButton
                          key={child.id}
                          component={Link}
                          to={child.groupPaths ? getGroupPath(child) : child.path}
                          onClick={() => handleMenuItemClick(child.id)}
                          selected={isChildActive(child)}
                          sx={{
                            pl: 4,
                            py: 0.75,
                            minHeight: 40,
                            borderRadius: "8px",
                            ml: 2,
                            mr: 1,
                            mb: 0.5,
                            backgroundColor:
                              isChildActive(child) ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            },
                            "&.Mui-selected": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              "&:hover": {
                                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                              },
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              color: isChildActive(child) ? "rgb(244, 177, 14)" : "inherit",
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={child.label}
                            primaryTypographyProps={{
                              fontSize: "0.875rem",
                              fontWeight: isChildActive(child) ? 600 : 400,
                              color: isChildActive(child) ? "rgb(244, 177, 14)" : "inherit",
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                )}

                {/* Menú flotante para modo colapsado (solo desktop) */}
                {!isMobileMode && isNavbarCollapsed && openMenus[menu.id] && (
                  <Box
                    data-dropdown="true"
                    sx={{
                      position: "fixed",
                      left: 72,
                      zIndex: 1300,
                      bgcolor: "background.paper",
                      boxShadow: 4,
                      borderRadius: 1,
                      width: 240,
                      top: 8,
                      maxHeight: "90vh",
                      overflowY: "auto",
                    }}
                  >
                    <List>
                      {menu.children.map((child) => (
                        <ListItemButton
                          key={child.id}
                          component={Link}
                          to={child.groupPaths ? getGroupPath(child) : child.path}
                          onClick={() => handleMenuItemClick(child.id)}
                          selected={isChildActive(child)}
                          sx={{
                            py: 1,
                            "&.Mui-selected": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              color: isChildActive(child) ? "rgb(244, 177, 14)" : "inherit",
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText primary={child.label} primaryTypographyProps={{ fontSize: "0.875rem" }} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Submenú en móvil: mostrar dentro del Drawer temporal cuando está abierto */}
                {isMobileMode && (
                  <Collapse in={openMenus[menu.id]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {menu.children.map((child) => (
                        <ListItemButton
                          key={child.id}
                          component={Link}
                          to={child.groupPaths ? getGroupPath(child) : child.path}
                          onClick={() => handleMenuItemClick(child.id)}
                          selected={isChildActive(child)}
                          sx={{
                            pl: 4,
                            py: 1,
                            minHeight: 44,
                            borderRadius: "8px",
                            ml: 1,
                            mr: 1,
                            mb: 0.5,
                            backgroundColor:
                              isChildActive(child) ? alpha(theme.palette.primary.main, 0.08) : "transparent",
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              color: isChildActive(child) ? "rgb(244, 177, 14)" : "inherit",
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText primary={child.label} primaryTypographyProps={{ fontSize: "0.95rem" }} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                )}
              </>
            )}
          </React.Fragment>
        ))}
      </List>

      {/* Sección de usuario */}
      <Box
        sx={{
          p: isNavbarCollapsed && !isMobileMode ? 1 : 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          display: "flex",
          flexDirection: isNavbarCollapsed && !isMobileMode ? "column" : "row",
          alignItems: "center",
          justifyContent: isNavbarCollapsed && !isMobileMode ? "center" : "space-between",
        }}
      >
        {user ? (
          <>
            {(!isNavbarCollapsed || isMobileMode) && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: theme.palette.primary.main,
                    fontSize: "1rem",
                  }}
                >
                  {user.nombreUsuario?.charAt(0).toUpperCase() || "U"}
                </Avatar>
                <Box sx={{ ml: 1.5, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                    {user.nombreUsuario}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {user.rolUsuario || "Usuario"}
                  </Typography>
                </Box>
              </Box>
            )}

            <Tooltip title="Cerrar sesión">
              <IconButton
                onClick={logout}
                size="small"
                sx={{
                  ml: isNavbarCollapsed && !isMobileMode ? 0 : 1,
                  mt: isNavbarCollapsed && !isMobileMode ? 1 : 0,
                  color: theme.palette.error.main,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.2) },
                }}
                aria-label="Cerrar sesión"
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Button
            component={Link}
            to="/login"
            variant="contained"
            fullWidth
            sx={{ borderRadius: "8px", textTransform: "none" }}
          >
            {isNavbarCollapsed && !isMobileMode ? <PersonIcon /> : "Iniciar sesión"}
          </Button>
        )}
      </Box>
    </Box>
  )

  // Render: en desktop, Drawer permanente; en móvil, Drawer temporal + FAB
  return (
    <>
      {isTabletDown ? (
        <>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              "aria-label": "Menú de navegación",
            }}
            sx={{
              zIndex: theme.zIndex.drawer + 2,
              "& .MuiDrawer-paper": {
                width: "min(86vw, 360px)",
                boxSizing: "border-box",
                backgroundColor: theme.palette.mode === "light" ? "#ffffff" : "#1e1e1e",
              },
            }}
          >
            <DrawerContent isMobileMode />
          </Drawer>
        </>
      ) : (
        <Drawer
          variant="permanent"
          open={!isNavbarCollapsed}
          sx={{
            width: isNavbarCollapsed ? 72 : drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: isNavbarCollapsed ? 72 : drawerWidth,
              boxSizing: "border-box",
              backgroundColor: theme.palette.mode === "light" ? "#ffffff" : "#1e1e1e",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
              transition: theme.transitions.create(["width"], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: "hidden",
            },
          }}
        >
          <DrawerContent />
        </Drawer>
      )}

      {/* Zona de activación para swipe gesture en mobile */}
      {isTabletDown && (
        <Box
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            width: '30px',
            height: '100vh',
            zIndex: theme.zIndex.drawer - 1,
            touchAction: 'pan-y', // Permite scroll vertical, previene back horizontal
            pointerEvents: mobileOpen ? 'none' : 'auto', // Desactivar cuando navbar está abierto
          }}
        />
      )}
    </>
  )
}

export default NavBar
